import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import EngineeringRoundedIcon from '@mui/icons-material/EngineeringRounded'
import IncidentsAPI from 'services/IncidentsAPI'
import { listContractors } from 'services/contractorService'
import SearchableSelect from 'components/SearchableSelect'

const VETTING_META = {
  pending_review: { label: 'Pending review', colour: '#9aa0aa' },
  approved:       { label: 'Approved',       colour: '#2aa6b3' },
  probationary:   { label: 'Probationary',   colour: '#d97706' },
  suspended:      { label: 'Suspended',      colour: '#b00020' },
}

const vettingLabel = status => VETTING_META[status]?.label || 'Not vetted'
const vettingColour = status => VETTING_META[status]?.colour || '#9aa0aa'

const IncidentContractorsSection = ({ incident, onUpdated }) => {
  const [orgContractors, setOrgContractors] = useState([])
  const [adding, setAdding] = useState(false)
  const [selectedId, setSelectedId] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [removingId, setRemovingId] = useState(null)

  const contractors = useMemo(() => incident.contractors || [], [incident.contractors])

  useEffect(() => {
    listContractors()
      .then(res => setOrgContractors(res?.data?.data?.contractors || []))
      .catch(() => {})
  }, [])

  const linkedIds = useMemo(
    () => new Set(contractors.filter(c => !c.removed).map(c => c.id)),
    [contractors],
  )

  const pickable = useMemo(
    () => orgContractors.filter(c => c.isActive && !linkedIds.has(c.id)),
    [orgContractors, linkedIds],
  )

  const resetForm = () => {
    setAdding(false)
    setSelectedId('')
    setNote('')
  }

  const handleAdd = async () => {
    if (!selectedId) return
    setSaving(true)
    try {
      await IncidentsAPI.addContractor(incident.id, {
        contractor_id: selectedId,
        note: note.trim() || null,
      })
      toast.success('Contractor linked.')
      resetForm()
      onUpdated?.()
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not link contractor.')
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async (contractorId) => {
    setRemovingId(contractorId)
    try {
      await IncidentsAPI.removeContractor(incident.id, contractorId)
      toast.success('Contractor removed.')
      onUpdated?.()
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not remove contractor.')
    } finally {
      setRemovingId(null)
    }
  }

  return (
    <Panel>
      <PanelHead>
        <PanelTitle>
          <EngineeringRoundedIcon style={{ fontSize: 15 }} />
          Involved contractors · {contractors.length}
        </PanelTitle>
        {!adding && (
          <AddBtn type="button" onClick={() => setAdding(true)}>
            <AddRoundedIcon style={{ fontSize: 13 }} />
            Add contractor
          </AddBtn>
        )}
      </PanelHead>

      {adding && (
        <AddForm>
          <Field>
            <FieldLabel>Contractor</FieldLabel>
            <SearchableSelect
              value={selectedId}
              onChange={v => setSelectedId(v)}
              options={pickable.map(c => ({ value: c.id, label: c.name, sublabel: c.company || undefined }))}
              placeholder='Select a contractor…'
              searchPlaceholder='Search contractors…'
              disabled={saving}
              ariaLabel='Contractor'
            />
            {pickable.length === 0 && (
              <EmptyHint>No further active contractors available to link.</EmptyHint>
            )}
          </Field>
          <Field>
            <FieldLabel>Note (optional)</FieldLabel>
            <NoteArea
              rows={2}
              value={note}
              placeholder='How were they involved? Scope, area, observations…'
              onChange={e => setNote(e.target.value)} />
          </Field>
          <FormActions>
            <CancelBtn type="button" onClick={resetForm}>Cancel</CancelBtn>
            <AddSubmitBtn type="button" disabled={!selectedId || saving} onClick={handleAdd}>
              Link contractor
            </AddSubmitBtn>
          </FormActions>
        </AddForm>
      )}

      {contractors.length === 0 && !adding && (
        <EmptyState>No contractors linked to this incident yet.</EmptyState>
      )}

      <ContractorList>
        {contractors.map(c => (
          <ContractorCard key={c.id} $removed={c.removed}>
            <CardMain>
              <NameRow>
                <Name>{c.name}</Name>
                {c.removed ? (
                  <RemovedTag>No longer active</RemovedTag>
                ) : (
                  <VettingChip $colour={vettingColour(c.vettingStatus)}>
                    {vettingLabel(c.vettingStatus)}
                  </VettingChip>
                )}
              </NameRow>
              {c.company && <Company>{c.company}</Company>}
              {c.note && <Note>{c.note}</Note>}
            </CardMain>
            <RemoveBtn
              type="button"
              title="Remove contractor"
              aria-label="Remove contractor"
              disabled={removingId === c.id}
              onClick={() => handleRemove(c.id)}>
              <CloseRoundedIcon style={{ fontSize: 16 }} />
            </RemoveBtn>
          </ContractorCard>
        ))}
      </ContractorList>
    </Panel>
  )
}

export default IncidentContractorsSection

const Panel = styled.div`
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex; flex-direction: column; gap: 14px;
  background: #fafbfc;
`
const PanelHead = styled.div`
  display: flex; align-items: center; gap: 10px;
`
const PanelTitle = styled.h3`
  display: inline-flex; align-items: center; gap: 6px;
  margin: 0;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: #0C1229;
`
const AddBtn = styled.button`
  font: inherit;
  margin-left: auto;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 11px;
  background: transparent;
  border: 1px dashed #cbcbd2;
  color: #6b7280;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
  &:hover { border-color: #33becc; color: #1f8c97; }
`
const AddForm = styled.div`
  display: flex; flex-direction: column; gap: 10px;
  padding: 12px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 10px;
`
const Field = styled.div` display: flex; flex-direction: column; gap: 4px; `
const FieldLabel = styled.label`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 10px;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  font-weight: 800;
  color: #6b7280;
`
const NoteArea = styled.textarea`
  font: inherit;
  padding: 9px 11px;
  border: 1px solid #ececec;
  border-radius: 8px;
  font-size: 12.5px;
  background: #fff;
  resize: vertical;
  outline: none;
  &:focus { border-color: #33becc; }
`
const EmptyHint = styled.span`
  font-size: 11px;
  color: #6b7280;
`
const FormActions = styled.div` display: flex; gap: 8px; justify-content: flex-end; `
const CancelBtn = styled.button`
  font: inherit;
  padding: 6px 14px;
  background: transparent;
  border: 1px solid #ececec;
  color: #0C1229;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
`
const AddSubmitBtn = styled.button`
  font: inherit;
  padding: 6px 14px;
  background: #2aa6b3;
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 800;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`
const EmptyState = styled.div`
  font-size: 12.5px;
  color: #6b7280;
  padding: 4px 2px;
`
const ContractorList = styled.div`
  display: flex; flex-direction: column; gap: 8px;
`
const ContractorCard = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  padding: 12px 14px;
  background: ${({ $removed }) => $removed ? '#f4f4f6' : '#fff'};
  border: 1px solid #ececec;
  border-radius: 10px;
  opacity: ${({ $removed }) => $removed ? 0.62 : 1};
`
const CardMain = styled.div`
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 4px;
`
const NameRow = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
`
const Name = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #0C1229;
`
const VettingChip = styled.span`
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $colour }) => $colour};
  background: ${({ $colour }) => `${$colour}1a`};
  border: 1px solid ${({ $colour }) => `${$colour}40`};
`
const RemovedTag = styled.span`
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
  background: #e9e9ed;
  border: 1px solid #d8d8de;
`
const Company = styled.span`
  font-size: 12px;
  color: #6b7280;
`
const Note = styled.p`
  margin: 2px 0 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: #1e293b;
  white-space: pre-wrap;
`
const RemoveBtn = styled.button`
  font: inherit;
  flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px;
  background: transparent;
  border: 1px solid #ececec;
  border-radius: 8px;
  color: #6b7280;
  cursor: pointer;
  &:hover { border-color: #b00020; color: #b00020; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`
